/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import localforage from "localforage";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";
import Loader from "examples/Loader";

const { GET } = ReqMethods;

// Material Dashboard 2 React main context
const AgencyContext = createContext();

// Setting custom name for the context which is visible on react dev tools
AgencyContext.displayName = "AgencyContext";

// Any mutation elsewhere that changes agency data should call
// queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEY }) to refresh
// every consumer of useAgencyInfo() - partial key matching means this
// invalidates regardless of what (if anything) follows "agency" in a key.
const AGENCY_QUERY_KEY = ["agency"];

// Material Dashboard 2 React context provider
function AgencyProvider({ children }) {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: AGENCY_QUERY_KEY,
    queryFn: async () => {
      const localAgency = await localforage.getItem("agency");
      if (!localAgency) return { agency: {}, agencyConfig: {} };

      const { response } = await request(GET, "/agency", {
        query: `_id=${localAgency._id}`,
      });
      if (!response) return { agency: { ...localAgency }, agencyConfig: {} };

      localforage.setItem("agency", { ...response.data });
      localforage.setItem("agency_config", { ...response.agency_config });
      return { agency: { ...response.data }, agencyConfig: { ...response.agency_config } };
    },
    staleTime: 60 * 1000,
  });

  const agency = data?.agency || {};
  const agencyConfig = data?.agencyConfig || {};

  const Update = (updates) => {
    queryClient.setQueryData(AGENCY_QUERY_KEY, (old) => ({
      ...old,
      agency: { ...old?.agency, ...updates },
    }));
  };

  const clear = () => {
    queryClient.setQueryData(AGENCY_QUERY_KEY, { agency: {}, agencyConfig: {} });
  };

  const UpdateAgencyConfig = (updates) => {
    queryClient.setQueryData(AGENCY_QUERY_KEY, (old) => {
      const nextConfig = { ...old?.agencyConfig, ...updates };
      localforage.setItem("agency_config", nextConfig);
      return { ...old, agencyConfig: nextConfig };
    });
  };

  // Lets any page force a refresh (e.g. after an action it knows changed
  // agency data some other way) without needing its own import of
  // queryClient.
  const handleRefresh = () => queryClient.invalidateQueries({ queryKey: AGENCY_QUERY_KEY });

  const value = useMemo(
    () => [agency, Update, clear, agencyConfig, UpdateAgencyConfig, handleRefresh],
    [agency, agencyConfig]
  );

  return isLoading ? (
    <Loader />
  ) : (
    <AgencyContext.Provider value={value}>{children}</AgencyContext.Provider>
  );
}

// Material Dashboard 2 React custom hook for using context
function useAgencyInfo() {
  return useContext(AgencyContext) || [];
}

// Typechecking props for the MaterialUIControllerProvider
AgencyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AgencyProvider, useAgencyInfo };
